import { useMemo, useCallback, useEffect, type ComponentProps } from "react";
import { type CassetteSliderSectionList } from "@userFrontend/components/CassetteSliderSectionList";
import { useSWRData } from "@root/shared/hooks/useSWRData";
import { sendErrorLog } from "@root/shared/utils/sendErrorLog";
import { type Cassette } from "./type";

export const useAfterKeepRecommendWorks = (
  fetchUrl: string,
  inView: boolean,
): ComponentProps<typeof CassetteSliderSectionList>["sections"] | undefined => {
  // NOTE: useSWRDataはデフォルトで再検証を行わないため、操作手順によってはレコメンド求人の最新情報が再フェッチされません。
  // それにより、レコメンド求人と、このファイルで生成しているキープ押下後レコメンド求人のキープステータスがズレる可能性があるため、再フェッチが走るように、再検証オプションを有効にします。
  const { data, mutate } = useSWRData<{ data: Cassette[] }>({
    url: fetchUrl,
    options: {
      revalidateIfStale: true,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      onError: (error: Error) => sendErrorLog(error),
    },
  });

  // NOTE:
  // キープ押下後レコメンド(当コンポーネント)が画面内に入った場合、
  // レコメンド求人(RecommendWorks)とキープボタンの見た目を一致させる目的で、
  // 再フェッチを走らせる。
  useEffect(() => {
    if (inView) {
      mutate();
    }
  }, [inView, mutate]);

  const createData = useCallback((cassettes: Cassette[]) => {
    return cassettes.map((item) => ({
      gaAction: "detail_keep_recommend",
      gaLabel: item.gaLabel,
      detailUrl: item.linkHref,
      imgSrc: item.imgSrc,
      dummyImgPath: item.dummyImgPath,
      shopOrBrandName: item.shopOrBrandName,
      businessTypeAndOccupation: item.businessTypeAndOccupation,
      title: item.title,
      salaries: item.salaryTexts,
      shopLocation: item.shopLocation,
      workCode: item.workCode,
      hasKeeped: item.keepButtonContent.hasKeeped,
      keepButtonUrl: item.keepButtonContent.keepButtonUrl,
      keepGaAction: "detail_keep_recommend_keep",
      keepGaLabel: item.gaLabel,
    }));
  }, []);

  const cassettesData = useMemo(
    () => (data?.data ? createData(data.data) : []),
    [data, createData],
  );

  const sections = useMemo(
    () => (cassettesData.length ? [{ cassettes: cassettesData }] : undefined),
    [cassettesData],
  );

  return sections;
};
